import { useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { isSupported } from '../lib/format-detect';

interface Props {
  onFile: (file: File) => void;
  empty: boolean;
}

export function DropZone({ onFile, empty }: Props) {
  const [hover, setHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? [])[0];
      if (file && isSupported(file)) onFile(file);
    };
    const onDragOver = (e: DragEvent) => { e.preventDefault(); setHover(true); };
    const onDragLeave = () => setHover(false);
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setHover(false);
      const file = Array.from(e.dataTransfer?.files ?? [])[0];
      if (file && isSupported(file)) onFile(file);
    };
    window.addEventListener('paste', onPaste);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [onFile]);

  if (!empty) {
    return (
      <div
        className={`pointer-events-none fixed inset-0 z-50 transition ${hover ? 'bg-[var(--theme)]/80 backdrop-blur' : 'opacity-0'}`}
        aria-hidden={!hover}
      >
        <div className="flex h-full items-center justify-center text-2xl">Drop to replace…</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className={`blueprint-grid flex h-[60vh] w-full flex-col items-center justify-center rounded-md border-2 border-dashed transition ${
        hover ? 'border-[var(--content)] bg-[var(--entry)]' : 'border-[var(--border)] hover:border-[var(--content)]'
      }`}
    >
      <Upload className="mb-3 h-10 w-10 text-[var(--content)]" strokeWidth={2} />
      <span className="text-lg">Drop, paste (⌘V), or click to upload</span>
      <span className="tech-label mt-3">PNG · JPG · WebP · AVIF · GIF · BMP · SVG · HEIC · PDF</span>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.avif,.gif,.bmp,.svg,.heic,.heif,.pdf,image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </button>
  );
}
